import React, { createContext, useState, useContext, useEffect, useCallback } from "react";

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [userData, setUserData] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    const login = (email, role, authToken, additionalData = {}) => {
        const newUser = { email, role, ...additionalData };
        setUserData(newUser);
        setToken(authToken);
        
        // Store both user data and token
        localStorage.setItem("userData", JSON.stringify(newUser));
        if (authToken) {
            localStorage.setItem("token", authToken);
        }
    };

    const logout = useCallback(() => {
        // Clear all states
        setUserData(null);
        setToken(null);
        
        // Clear all storage
        localStorage.removeItem("userData");
        localStorage.removeItem("token");
        localStorage.removeItem("sidebarFrom");
        
        // Optional: Clear any other app-specific storage
        // sessionStorage.clear();
        
        console.log("Logout completed, storage cleared");
    }, []);

    // Load stored data on mount
    useEffect(() => {
        const initializeAuth = () => {
            try {
                const storedUser = localStorage.getItem("userData");
                const storedToken = localStorage.getItem("token");
                
                if (storedUser && storedToken) {
                    const parsedUser = JSON.parse(storedUser);
                    setUserData(parsedUser);
                    setToken(storedToken);
                }
            } catch (error) {
                console.error("Error loading auth data:", error);
                // Clear corrupted data
                localStorage.removeItem("userData");
                localStorage.removeItem("token");
            } finally {
                setLoading(false);
            }
        };

        initializeAuth();
    }, []);

    // Check if token is expired (optional)
    const isTokenExpired = useCallback(() => {
        if (!token) return true;
        
        try {
            // Decode token and check expiration
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const payload = JSON.parse(window.atob(base64));
            
            // Check if token is expired
            return payload.exp < Date.now() / 1000;
        } catch (error) {
            console.error("Error checking token expiration:", error);
            return true;
        }
    }, [token]);

    // Auto logout if token expired
    useEffect(() => {
        if (token && isTokenExpired()) {
            console.log("Token expired, logging out");
            logout();
        }
    }, [token, isTokenExpired, logout]);

    const value = {
        userData,
        token,
        login,
        logout,
        isAuthenticated: !!token && !!userData && !isTokenExpired(),
        loading
    };

    if (loading) {
        return <div>Loading...</div>; // Or return null, or a spinner
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};